/**
 * Cancellation journey (E -> F1/F3/F3b -> G -> H -> I), platform-free.
 * The retention discount (F2) and the Pro-to-Plus switch (F2b) were dropped
 * on 2026-09-23: no store or billing offer exists to back them.
 *
 * Billing providers own cancellation. Nothing here changes billing: every
 * branch ends at the provider handoff (H), and only a synced snapshot with
 * `willRenew === false` may show "Renewal is off" (I).
 *
 * Supersedes `lib/cancel-retention.ts` + `hooks/useCancelPlanFlow.ts`; the
 * still-valid helpers below keep their names and behaviour.
 */
import type {
  CancellationFeedbackInput,
  CancellationReason,
  CancellationReasonDetail,
  CancellationReasonDetailMap,
  PaidPlanTier,
  PlanSet,
  SubscriptionCapabilities,
  SubscriptionSnapshot,
} from '@tabbio/types';

/* ------------------------------------------------------------------------ */
/* Still-valid helpers (moved from lib/cancel-retention.ts)                 */
/* ------------------------------------------------------------------------ */

export type CancelDestination = 'app_store' | 'play_store' | 'portal';

export function getCancelDestination(
  store: SubscriptionSnapshot['store'] | undefined,
): CancelDestination {
  if (store === 'app_store' || store === 'play_store') return store;
  return 'portal';
}

export function canStartCancellation(
  snapshot: SubscriptionSnapshot | null,
): boolean {
  return Boolean(
    snapshot &&
      snapshot.tier !== 'free' &&
      snapshot.willRenew !== false &&
      snapshot.source !== 'admin_grant' &&
      !['expired', 'inactive', 'cancelled', 'canceled'].includes(
        snapshot.status,
      ),
  );
}

/** A journey belongs to one billing context; usage/date refreshes keep it open. */
export function getCancellationScope(
  snapshot: SubscriptionSnapshot | null,
  companyId?: string | null,
): string {
  return JSON.stringify([
    companyId ?? null,
    snapshot?.planSet,
    snapshot?.tier,
    snapshot?.store,
    snapshot?.willRenew,
    snapshot?.status,
    snapshot?.source,
  ]);
}

export function formatCancelAccessDate(
  value: string | null | undefined,
  locale?: string,
): string | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  return new Intl.DateTimeFormat(locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

export function getCancelAccessDate(
  snapshot: SubscriptionSnapshot | null,
  now = Date.now(),
): string | null {
  // A renewal date in the past (e.g. billing grace) is not a promise of access.
  // Trials can have different early-cancellation rules, so defer to the store.
  if (!snapshot || snapshot.status === 'trialing') return null;
  return snapshot.renewsAt && Date.parse(snapshot.renewsAt) > now
    ? snapshot.renewsAt
    : null;
}

/** Categorical only: never account, payment, management-URL or free-text data. */
export function getCancelAnalyticsProperties(input: {
  surface: 'native' | 'web';
  snapshot: SubscriptionSnapshot | null;
}) {
  return {
    surface: input.surface,
    store: input.snapshot?.store ?? 'unknown',
    planSet: input.snapshot?.planSet ?? 'individual',
    tier: input.snapshot?.tier ?? 'free',
    billingInterval: input.snapshot?.billingInterval ?? 'unknown',
    // 3 = the subscription-journeys redesign (2 was the single-sheet review).
    journeyVersion: 3,
  };
}

/* ------------------------------------------------------------------------ */
/* Reasons, details, attribution                                            */
/* ------------------------------------------------------------------------ */

export type CancelReason = CancellationReason;

/** Order and icons as `reasonList` in the design reference. */
export const CANCEL_REASON_LIST: readonly {
  reason: CancelReason;
  /** HugeIcons name hint; web maps it to a Material Symbol. */
  icon: 'briefcase' | 'pause' | 'wallet' | 'thumbDown' | 'help' | 'bug' | 'ellipsis';
  labelKey: string;
}[] = [
  { reason: 'found_job', icon: 'briefcase', labelKey: 'subscription.cancel.reasons.options.found_job' },
  { reason: 'later', icon: 'pause', labelKey: 'subscription.cancel.reasons.options.later' },
  { reason: 'price', icon: 'wallet', labelKey: 'subscription.cancel.reasons.options.price' },
  { reason: 'value', icon: 'thumbDown', labelKey: 'subscription.cancel.reasons.options.value' },
  { reason: 'confusing', icon: 'help', labelKey: 'subscription.cancel.reasons.options.confusing' },
  { reason: 'broken', icon: 'bug', labelKey: 'subscription.cancel.reasons.options.broken' },
  { reason: 'other', icon: 'ellipsis', labelKey: 'subscription.cancel.reasons.options.other' },
];

export const CANCEL_REASONS: readonly CancelReason[] = CANCEL_REASON_LIST.map(
  (item) => item.reason,
);

/** Chips on the help screen (F3). Pinned to the shared union the API validates. */
export const CANCEL_DETAIL_OPTIONS = {
  found_job: ['cv', 'jobboard', 'recruiter', 'agent', 'elsewhere', 'other'],
  later: [],
  price: [],
  value: ['cv', 'matches', 'results', 'other'],
  confusing: ['cv', 'matches', 'nova'],
  broken: ['crash', 'loading', 'failed', 'other'],
  other: ['missing', 'switching', 'other'],
} as const satisfies {
  [Reason in CancelReason]: readonly CancellationReasonDetailMap[Reason][];
};

/** "How did Tabbio help?" - the found-a-job attribution chips. */
export const CAREER_ATTRIBUTION_OPTIONS = CANCEL_DETAIL_OPTIONS.found_job;
/** Attribution chips that credit Tabbio (styled as Tabbio choices). */
export const TABBIO_ATTRIBUTIONS: ReadonlySet<string> = new Set([
  'cv',
  'jobboard',
  'recruiter',
  'agent',
]);

export function isDetailFor(
  reason: CancelReason,
  detail: string | null | undefined,
): detail is CancellationReasonDetail {
  return (
    typeof detail === 'string' &&
    (CANCEL_DETAIL_OPTIONS[reason] as readonly string[]).includes(detail)
  );
}

export function detailLabelKey(reason: CancelReason, detail: string) {
  return reason === 'found_job'
    ? `subscription.cancel.career.attribution.${detail}`
    : `subscription.cancel.details.${reason}.${detail}`;
}

/* ------------------------------------------------------------------------ */
/* Router                                                                    */
/* ------------------------------------------------------------------------ */

export type CancelJourneyStep =
  | 'reason'
  | 'help'
  | 'career'
  | 'pause'
  | 'confirm'
  | 'provider'
  | 'renewal-off';

export type ProviderIntent = 'cancel' | 'pause' | 'renew' | 'manage';

export interface CancelJourneyContext {
  capabilities: SubscriptionCapabilities;
  accountCycle: 'monthly' | 'yearly' | null;
  tier: 'free' | PaidPlanTier;
}

export const DEFAULT_CANCEL_CONTEXT: CancelJourneyContext = {
  capabilities: { pause: false },
  accountCycle: null,
  tier: 'free',
};

/** Native routes (settings stack). Web maps steps to dialog views instead. */
export const CANCEL_JOURNEY_ROUTES: Record<CancelJourneyStep, string> = {
  reason: '/settings/cancel',
  help: '/settings/cancel/help',
  career: '/settings/cancel/career',
  pause: '/settings/cancel/pause',
  confirm: '/settings/cancel/confirm',
  provider: '/settings/cancel/provider',
  'renewal-off': '/settings/cancel/renewal-off',
};

/**
 * After E ("Continue"). `null` = skipped. `later` always lands on the pause
 * screen, which renders its "turn off renewal" variant when
 * `capabilities.pause` is false. Every other path ends on the confirm screen
 * (G): there is no retention discount.
 */
export function resolveAfterReason(
  reason: CancelReason | null,
  ctx: CancelJourneyContext,
): CancelJourneyStep {
  switch (reason) {
    case 'found_job':
      return 'help';
    case 'later':
      return 'pause';
    case 'price':
      // Yearly: "Find a plan that fits." with the compare-plans row.
      return ctx.accountCycle === 'yearly' ? 'help' : 'confirm';
    case 'value':
    case 'confusing':
    case 'broken':
    case 'other':
      return 'help';
    case null:
      return 'confirm';
  }
}

export function resolveAfterHelp(
  reason: CancelReason | null,
  _ctx: CancelJourneyContext,
): CancelJourneyStep {
  return reason === 'found_job' ? 'career' : 'confirm';
}

export function resolveAfterCareer(_ctx: CancelJourneyContext): CancelJourneyStep {
  return 'confirm';
}

export function resolveAfterPauseDeclined(
  _ctx: CancelJourneyContext,
): CancelJourneyStep {
  return 'confirm';
}

/** Where "<" returns from H, by intent (design reference `provider()`). */
export function providerBackStep(
  intent: ProviderIntent,
): CancelJourneyStep | 'billing' {
  if (intent === 'renew') return 'renewal-off';
  if (intent === 'pause') return 'pause';
  if (intent === 'manage') return 'billing';
  return 'confirm';
}

/** Step 3 of "Managed in {provider}". `common` namespace. */
export const PROVIDER_INTENT_STEP_KEYS: Record<ProviderIntent, string> = {
  cancel: 'subscription.cancel.provider.intents.cancel',
  pause: 'subscription.cancel.provider.intents.pause',
  renew: 'subscription.cancel.provider.intents.renew',
  manage: 'subscription.cancel.provider.intents.manage',
};

/* ------------------------------------------------------------------------ */
/* Screen content selectors                                                  */
/* ------------------------------------------------------------------------ */

export type NovaMoodName = 'calm' | 'happy' | 'sad' | 'sleeping' | 'farewell';

export function isSuccessExit(reason: CancelReason | null) {
  return reason === 'found_job';
}

/** G: happy for a found job, sleeping for a break, sad otherwise. */
export function getConfirmMood(reason: CancelReason | null): NovaMoodName {
  if (isSuccessExit(reason)) return 'happy';
  if (reason === 'later') return 'sleeping';
  return 'sad';
}

export interface HelpScreenContent {
  kind: 'career' | 'price' | 'detail';
  titleKey: string;
  mood: NovaMoodName;
  /** Detail chips (label keys resolved with `detailLabelKey`). */
  options: readonly string[];
  /** Label above the note; null when no note is shown. */
  promptKey: string | null;
  placeholderKey: string | null;
  noteKey: string | null;
  /** F3 shows "Keep my plan" under Continue except on the career variant. */
  showKeepPlan: boolean;
}

const HELP_TITLE_KEYS: Record<CancelReason, string> = {
  found_job: 'subscription.cancel.help.titles.foundJob',
  later: 'subscription.cancel.help.titles.other',
  price: 'subscription.cancel.help.titles.price',
  value: 'subscription.cancel.help.titles.value',
  confusing: 'subscription.cancel.help.titles.confusing',
  broken: 'subscription.cancel.help.titles.broken',
  other: 'subscription.cancel.help.titles.other',
};

function helpPromptKey(reason: CancelReason, detail: string | null) {
  if (detail === 'missing') return 'subscription.cancel.help.prompts.missing';
  if (detail === 'switching') return 'subscription.cancel.help.prompts.switching';
  if (reason === 'broken') return 'subscription.cancel.help.prompts.broken';
  if (reason === 'confusing') return 'subscription.cancel.help.prompts.confusing';
  if (reason === 'value') return 'subscription.cancel.help.prompts.value';
  return 'subscription.cancel.help.prompts.default';
}

export function getHelpContent(
  reason: CancelReason,
  detail: string | null,
): HelpScreenContent {
  if (reason === 'found_job') {
    // The note appears only for "Not from Tabbio" / "Other".
    const needsNote = detail === 'elsewhere' || detail === 'other';
    return {
      kind: 'career',
      titleKey: HELP_TITLE_KEYS.found_job,
      mood: 'happy',
      options: CAREER_ATTRIBUTION_OPTIONS,
      promptKey: needsNote ? 'subscription.cancel.help.career.tellMore' : null,
      placeholderKey: needsNote
        ? 'subscription.cancel.help.career.placeholder'
        : null,
      noteKey: needsNote ? 'subscription.cancel.help.career.note' : null,
      showKeepPlan: false,
    };
  }
  if (reason === 'price') {
    return {
      kind: 'price',
      titleKey: HELP_TITLE_KEYS.price,
      mood: 'calm',
      options: [],
      promptKey: null,
      placeholderKey: null,
      noteKey: null,
      showKeepPlan: true,
    };
  }
  return {
    kind: 'detail',
    titleKey: HELP_TITLE_KEYS[reason],
    mood: 'calm',
    options: CANCEL_DETAIL_OPTIONS[reason],
    promptKey: helpPromptKey(reason, detail),
    placeholderKey: 'subscription.cancel.help.placeholder',
    noteKey: 'subscription.cancel.help.note',
    showKeepPlan: true,
  };
}

/* ------------------------------------------------------------------------ */
/* Feedback payload                                                          */
/* ------------------------------------------------------------------------ */

export const CANCEL_NOTE_MAX_LENGTH = 1000;

export function cleanCancelNote(value: string | null | undefined) {
  return String(value ?? '')
    .replace(/\u0000/g, '')
    .trim()
    .slice(0, CANCEL_NOTE_MAX_LENGTH);
}

/**
 * The optional feedback for a Continue on F3, or null when there is nothing to
 * send (no chip and no note). Never includes identity; free text never goes to
 * analytics.
 */
export function buildCancellationFeedback(input: {
  reason: CancelReason | null;
  detail: string | null;
  draft: string | null | undefined;
  locale: 'en' | 'ar';
  planSet: PlanSet;
  tier: 'free' | PaidPlanTier;
  platform?: CancellationFeedbackInput['platform'];
}): Omit<CancellationFeedbackInput, 'idempotencyKey'> | null {
  if (!input.reason || input.tier === 'free') return null;
  const detail = isDetailFor(input.reason, input.detail) ? input.detail : null;
  const message = cleanCancelNote(input.draft);
  if (!detail && !message) return null;
  return {
    reason: input.reason,
    detail,
    message: message || null,
    locale: input.locale,
    planSet: input.planSet,
    tier: input.tier,
    ...(input.platform ? { platform: input.platform } : {}),
  };
}

/** Same payload -> same idempotency key, so a retry never creates a second record. */
export function feedbackFingerprint(
  payload: Omit<CancellationFeedbackInput, 'idempotencyKey'>,
) {
  return JSON.stringify([
    payload.reason,
    payload.detail ?? null,
    payload.message ?? null,
    payload.locale,
    payload.planSet,
    payload.tier,
  ]);
}

export function createIdempotencyKey(random: () => number = Math.random) {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  const part = () => Math.floor(random() * 0xffffffff).toString(16).padStart(8, '0');
  return `${Date.now().toString(16)}-${part()}-${part()}`;
}

/* ------------------------------------------------------------------------ */
/* Analytics                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Event names. The `subscription_cancel_*` family predates the redesign and is
 * kept; the rest are new. Properties are categorical; never free text.
 */
export const CANCEL_ANALYTICS_EVENTS = {
  flowOpened: 'subscription_cancel_flow_opened',
  flowClosed: 'subscription_cancel_flow_closed',
  reasonSelected: 'subscription_cancel_reason_selected',
  reasonSubmitted: 'subscription_cancel_reason_submitted',
  storeHandoff: 'subscription_cancel_store_handoff',
  storeOpened: 'subscription_cancel_store_opened',
  storeOpenFailed: 'subscription_cancel_store_open_failed',
  detailSelected: 'cancel_reason_detail_selected',
  careerAttributionSelected: 'career_success_attribution_selected',
  saveOfferViewed: 'save_offer_viewed',
  saveOfferDeclined: 'save_offer_declined',
  saveOfferAccepted: 'save_offer_accepted',
  lossScreenViewed: 'loss_screen_viewed',
  providerManagementOpened: 'provider_management_opened',
  retentionHelpOpened: 'retention_help_opened',
} as const;

export type CancelAnalyticsEvent =
  (typeof CANCEL_ANALYTICS_EVENTS)[keyof typeof CANCEL_ANALYTICS_EVENTS];

/* ------------------------------------------------------------------------ */
/* Journey store                                                             */
/* ------------------------------------------------------------------------ */

export interface CancelJourneyState {
  active: boolean;
  /** `getCancellationScope` at start; a changed billing context ends the journey. */
  scope: string | null;
  reason: CancelReason | null;
  /** Detail for the current reason; cleared when a different reason is chosen. */
  detail: string | null;
  /** Notes per reason, kept across back navigation and reason changes. */
  drafts: Partial<Record<CancelReason, string>>;
  context: CancelJourneyContext;
  providerIntent: ProviderIntent;
  /** Payload fingerprint -> idempotency key. */
  requestKeys: Record<string, string>;
  feedbackStatus: 'idle' | 'pending' | 'received' | 'error';
}

export const INITIAL_CANCEL_JOURNEY_STATE: CancelJourneyState = {
  active: false,
  scope: null,
  reason: null,
  detail: null,
  drafts: {},
  context: DEFAULT_CANCEL_CONTEXT,
  providerIntent: 'cancel',
  requestKeys: {},
  feedbackStatus: 'idle',
};

type Listener = () => void;

/**
 * Module-level journey store. Native pushes one route per step, so state must
 * outlive each screen: E calls `start()`, later screens read it, and closing
 * the journey or "Keep my plan" calls `clear()`.
 */
export class CancelJourneyStore {
  private state: CancelJourneyState = INITIAL_CANCEL_JOURNEY_STATE;
  private listeners = new Set<Listener>();

  getState = () => this.state;

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private set(patch: Partial<CancelJourneyState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Begins (or resumes) a journey for a billing scope. Re-entering E for the
   * same scope keeps reason, detail and drafts; a different scope starts over.
   */
  start(scope: string, context: Partial<CancelJourneyContext> = {}) {
    if (this.state.active && this.state.scope === scope) {
      this.setContext(context);
      return;
    }
    this.set({
      ...INITIAL_CANCEL_JOURNEY_STATE,
      active: true,
      scope,
      context: { ...DEFAULT_CANCEL_CONTEXT, ...context },
    });
  }

  setContext(context: Partial<CancelJourneyContext>) {
    this.set({ context: { ...this.state.context, ...context } });
  }

  /** Tapping the selected reason again clears it (design reference). */
  selectReason(reason: CancelReason) {
    const next = this.state.reason === reason ? null : reason;
    // A new reason starts without a chip; its note (if any) is kept.
    this.set({ reason: next, detail: null, feedbackStatus: 'idle' });
    return next;
  }

  selectDetail(detail: string | null) {
    const reason = this.state.reason;
    if (!reason) return null;
    const next =
      detail === null || this.state.detail === detail || !isDetailFor(reason, detail)
        ? null
        : detail;
    this.set({ detail: next, feedbackStatus: 'idle' });
    return next;
  }

  setDraft(text: string) {
    const reason = this.state.reason;
    if (!reason) return;
    this.set({
      drafts: {
        ...this.state.drafts,
        [reason]: text.slice(0, CANCEL_NOTE_MAX_LENGTH),
      },
      feedbackStatus: 'idle',
    });
  }

  getDraft(reason: CancelReason | null = this.state.reason) {
    return reason ? (this.state.drafts[reason] ?? '') : '';
  }

  setProviderIntent(intent: ProviderIntent) {
    this.set({ providerIntent: intent });
  }

  setFeedbackStatus(status: CancelJourneyState['feedbackStatus']) {
    this.set({ feedbackStatus: status });
  }

  /** Reuses the key for an unchanged payload so a resend is idempotent. */
  requestKeyFor(
    payload: Omit<CancellationFeedbackInput, 'idempotencyKey'>,
    createKey: () => string = createIdempotencyKey,
  ) {
    const fingerprint = feedbackFingerprint(payload);
    const existing = this.state.requestKeys[fingerprint];
    if (existing) return existing;
    const key = createKey();
    this.set({ requestKeys: { ...this.state.requestKeys, [fingerprint]: key } });
    return key;
  }

  nextAfterReason() {
    return resolveAfterReason(this.state.reason, this.state.context);
  }

  nextAfterHelp() {
    return resolveAfterHelp(this.state.reason, this.state.context);
  }

  nextAfterCareer() {
    return resolveAfterCareer(this.state.context);
  }

  nextAfterPauseDeclined() {
    return resolveAfterPauseDeclined(this.state.context);
  }

  clear() {
    this.set(INITIAL_CANCEL_JOURNEY_STATE);
  }
}

/** The app-wide journey. `useCancelJourney` binds it to React. */
export const cancelJourneyStore = new CancelJourneyStore();
